
<template>
  <div>
    <DialogResumePayment 
      ref="resume_payment" 
      @submitPayment="submitPix" 
      :selected_vrts="selected_vrts_data.selected_vrts" 
      :payment_ammount="payment_total" 
      :bonus_ammount="bonus_ammount"
      wait_client_payment
    ></DialogResumePayment>

    <v-dialog v-model="dialog" scrollable max-width="1100px" persistent transition="dialog-bottom-transition">
      <v-card class="charge-dialog-card">
        <div class="charge-dialog-header charge-dialog-header--pix">
          <div class="d-flex align-center">
            <v-avatar color="white" size="40" class="mr-3 elevation-1">
              <v-icon color="teal darken-2">mdi-qrcode</v-icon>
            </v-avatar>
            <div>
              <div class="headline white--text mb-0">Novo PIX</div>
              <div class="caption white--text" style="opacity:.85">
                PIX instantâneo vinculado às faturas do cliente · valor mínimo R$ 20,00
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="goBack()"><v-icon>mdi-close</v-icon></v-btn>
          </div>
        </div>

        <v-card-text class="pa-5">
          <v-form ref="new_pix" lazy-validation>
            <v-row dense class="mb-2">
              <v-col cols="12">
                <SelectModality :object_with_model="new_pix" custom_label="Módulo" outlined dense preselect_active/>
              </v-col>
              <v-col cols="12" sm="8">
                <v-autocomplete 
                  label="Cliente"
                  prepend-inner-icon="mdi-account" 
                  item-value="id" 
                  :item-text="formatClientDisplayName"
                  v-model="new_pix.client_id" 
                  :items="all_clients" 
                  :filter="customFilter" 
                  :rules="[rules.required]"
                  :loading="loading"
                  :disabled="!new_pix.place_id"
                  :hint="!new_pix.place_id ? 'Selecione o módulo primeiro' : ''"
                  persistent-hint
                  outlined
                  dense
                  hide-details="auto"
                >
                  <template v-slot:no-data> <span class="px-4"> Cliente não cadastrado </span> </template>
                  <template v-slot:selection="data"> {{ formatClientDisplayName(data.item) }} <span v-if="data.item.attributes.fantasyName" class="ml-1">({{ data.item.attributes.fantasyName }})</span> </template>
                  <template v-slot:item="data">
                    <v-list-item-content>
                      <v-list-item-title>Nome/Fantasia: <b>{{ data.item.attributes.fantasyName }}</b></v-list-item-title>
                      <v-list-item-subtitle>Razão Social: <b>{{ formatClientDisplayName(data.item) }}</b></v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field 
                  v-model.lazy="new_pix.value_aux" 
                  v-money="money" 
                  label="Valor"
                  prefix="R$"
                  prepend-inner-icon="mdi-currency-brl" 
                  :rules="[rules.required, rules.notZero, rules.minVal]"
                  outlined
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="8">
                <v-select
                  label="Empresa"
                  v-model="new_pix.company_key"
                  :items="sicoob_credential_options"
                  item-value="key"
                  item-text="name"
                  :rules="[rules.required]"
                  prepend-inner-icon="mdi-domain"
                  outlined
                  dense
                  hide-details="auto"
                  @change="handleCompanyKeyChange"
                />
              </v-col>
              <v-col cols="12" sm="4" v-if="pix_wit_due_date">
                <v-menu
                  v-model="modal_expire"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field 
                      :value="$moment(new_pix.due_date).add(12, 'hour').format('DD/MM/YYYY')"
                      label="Expira em" 
                      prepend-inner-icon="mdi-calendar" 
                      readonly
                      outlined
                      dense
                      hide-details="auto"
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker 
                    v-model="new_pix.due_date" 
                    color="teal darken-2" 
                    locale="pt-br" 
                    :min="today"
                    @input="modal_expire = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <v-row dense class="my-3">
              <v-col cols="12" sm="4">
                <div class="charge-summary-card d-flex align-center">
                  <v-avatar size="40" color="green lighten-5" class="mr-3">
                    <v-icon color="green darken-2">mdi-cash-check</v-icon>
                  </v-avatar>
                  <div>
                    <div class="caption grey--text text--darken-1">Faturas / Pagamento</div>
                    <div v-if="!has_set_payment" class="body-2 font-italic error--text">Informe o valor do PIX</div>
                    <div v-else-if="!vrts_is_selected" class="body-2 font-italic error--text">Selecione as faturas abaixo</div>
                    <div v-else class="subtitle-1 font-weight-bold">
                      <span :class="{ 'error--text': !paying_vrts_ammount_is_valid }">{{ formatMoney(paying_vrts_ammount) }}</span>
                      <span class="caption font-weight-regular"> / {{ formatMoney(payment_total) }}</span>
                    </div>
                    <div v-if="has_set_payment && vrts_is_selected && !paying_vrts_ammount_is_valid" class="caption error--text">
                      Valor das faturas maior que o PIX
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="charge-summary-card d-flex align-center">
                  <v-avatar size="40" color="teal lighten-5" class="mr-3">
                    <v-icon color="teal darken-2">mdi-cash-multiple</v-icon>
                  </v-avatar>
                  <div>
                    <div class="caption grey--text text--darken-1">Bônus sendo gerado</div>
                    <div v-if="has_set_payment" class="subtitle-1 font-weight-bold">{{ formatMoney(bonus_ammount) }}</div>
                    <div v-else class="body-2 font-italic grey--text">—</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="charge-summary-card d-flex align-center">
                  <v-avatar size="40" color="red lighten-5" class="mr-3">
                    <v-icon color="red darken-2">mdi-book-open-variant-outline</v-icon>
                  </v-avatar>
                  <div>
                    <div class="caption grey--text text--darken-1">Em aberto ({{ modality_name }})</div>
                    <div class="subtitle-1 font-weight-bold">{{ formatMoney(selected_vrts_data.total_debt) }}</div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <div
              v-if="has_set_payment && vrts_is_selected"
              class="charge-sum-box mb-4"
              :class="paying_vrts_ammount_is_valid ? 'charge-sum-box--ok' : 'charge-sum-box--err'"
            >
              <div class="d-flex justify-space-between align-center mb-1">
                <span class="caption font-weight-medium">Faturas cobertas pelo PIX</span>
                <span class="subtitle-2 font-weight-bold">
                  {{ formatMoney(paying_vrts_ammount) }}
                  <span class="caption font-weight-regular"> / {{ formatMoney(payment_total) }}</span>
                </span>
              </div>
              <v-progress-linear
                :value="sumProgress"
                height="6"
                rounded
                :color="paying_vrts_ammount_is_valid ? 'success' : 'warning'"
              ></v-progress-linear>
            </div>

            <VrtSelection v-if="new_pix.client_id" :client_id="new_pix.client_id" @onChange="handleChangeVrtSelection"></VrtSelection>
            <div v-else class="charge-empty-box text-center pa-6">
              <v-icon size="40" color="blue-grey lighten-3">mdi-account-search-outline</v-icon>
              <div class="body-2 grey--text mt-2">Escolha um cliente para exibir as faturas.</div>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="goBack()">Cancelar</v-btn>
          <v-btn
            color="teal darken-2"
            depressed
            class="px-5 white--text"
            :disabled="!pix_ready"
            @click="showResume()"
          >
            <v-icon left small>mdi-check</v-icon>
            Gerar PIX
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <dialog-loading v-if="saving"></dialog-loading>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import Api from '@/api/index'
import { VMoney } from 'v-money'
import sicoob_credentials from '@/_backoffice/_financial/mixins/sicoob_credentials'
import clientDisplay from '@/mixins/clientDisplay'

export default {
  name: 'CreatePix',
  directives: { money: VMoney },
  mixins: [sicoob_credentials, clientDisplay],
  components:{
    VrtSelection: () => import('@/_backoffice/_financial/components/order_payment/VrtSelection.vue'),
    DialogResumePayment: () => import('@/_backoffice/_financial/components/order_payment/DialogResumePayment.vue'),
    SelectModality: () => import('@/components/shared/SelectModality.vue')
  },
  data() {
    return {
      rules: {
        required: v => !!v || 'Campo é necessário',
        notZero: v => v != '0.00' || 'Não pode ser zero',
        minVal: v => !!v && Number(v.replace(/,/g, '')) >= 20 || 'Valor mínimo é R$ 20,00',
      },
      money: {
        decimal: '.',
        thousands: ',',
        precision: 2,
        masked: true
      },
      dialog: true,
      loading: false,
      saving: false,
      modal_expire: false,
      all_clients: [],
      new_pix: {
        client_id: null,
        due_date: this.$moment().endOf('month').format('YYYY-MM-DD'),
        value_aux: '0.0',
        company: null,
        bank: null,
        place_id: null,

        total_amount: '0',
        company_key: '',
        pix_type: 'order',
        pix_origin: 'avulso',

        created_by_id: null,
        created_by_name: null,
        payment_attributes: {

        }
      },
      selected_vrts_data:{
        paying_vrts: [],
        selected_vrts: [],
        selected_vrts_ammount: '0.00',
        total_debt: '0.00',
      },
    }
  },
  methods: {
    parseMoney(value) {
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0
      const raw = String(value ?? '').trim()
      if (!raw) return 0
      const hasComma = raw.includes(',')
      const hasDot = raw.includes('.')
      if (hasComma && hasDot) {
        if (raw.lastIndexOf(',') > raw.lastIndexOf('.')) {
          return Number(raw.replace(/\./g, '').replace(',', '.')) || 0
        }
        return Number(raw.replace(/,/g, '')) || 0
      }
      if (hasComma) return Number(raw.replace(/\./g, '').replace(',', '.')) || 0
      return Number(raw) || 0
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    showResume(){
      if (!String(this.new_pix.company_key || '').trim()) {
        this.addNotification({ type: 'error', msg: 'Selecione uma empresa (company key) para gerar o PIX.' })
        return
      }
      if (!this.hasSicoobCredentialKey(this.new_pix.company_key)) {
        this.addNotification({ type: 'error', msg: 'Selecione uma empresa com credenciais cadastradas para gerar o PIX.' })
        return
      }
      if (this.$refs.new_pix.validate() && this.selected_vrts_data.selected_vrts.length > 0) {
        this.$refs.resume_payment.openDialog()
      }else{
        this.addNotification({ type: 'error', msg: 'Verifique todos os campos!' })
      }
    },
    goBack() {
      this.dialog = false
      this.$emit('close')
      if (this.$route.name === 'CreatePix') this.$router.push({ name: 'AllPix' })
    },
    handleChangeVrtSelection( data ){
      this.selected_vrts_data = {...data}
    },
    preparePix(){
      this.new_pix.total_amount = this.new_pix.value_aux.replace(/,/g, '')
      this.new_pix.company_key = String(this.new_pix.company_key || '').trim()
      this.new_pix.created_by_id = this.current_user_employee_id
      this.new_pix.created_by_name = this.current_user_employee_name
      if (!this.pix_wit_due_date) {
        this.new_pix.due_date = null
      }

      this.new_pix.payment_attributes = {
        client_id: this.new_pix.client_id, 
        kind: 'pix', 
        total_value: this.new_pix.value_aux, 
        status_id: 6, 
        place_id: this.new_pix.place_id, 
        
        vrt_payments_attributes: this.selected_vrts_data.paying_vrts.map(vrt => ({
          vrt_transaction_id: vrt.vrt_transaction_id,
          paying_ammount: Number(vrt.paying_ammount).toFixed(2),
          paying_juros: Number(vrt.paying_juros || 0).toFixed(2),
        })),
      }
      this.new_pix.create_with_vrt_payment = true
    },
    submitPix() {
      if (!String(this.new_pix.company_key || '').trim()) {
        this.addNotification({ type: 'error', msg: 'Selecione uma empresa (company key) para gerar o PIX.' })
        return
      }
      if (!this.hasSicoobCredentialKey(this.new_pix.company_key)) {
        this.addNotification({ type: 'error', msg: 'Selecione uma empresa com credenciais cadastradas para gerar o PIX.' })
        return
      }
      if (this.$refs.new_pix.validate()) {
        this.preparePix()

        this.saving = true
        let req_status = false
        Api.Pix.create(this.new_pix).then(r => r.data).then(d => {
          req_status = true
          this.addNotification({type: 'success',  msg: `Pix gerado com sucesso!`})
          this.$emit('done', d)
        }).catch((err) => {
          console.log({ err });
          this.addNotification({
            type: 'error',  
            msg: err.response?.data?.['payment.vrt_payments.base']?.[0] || 'Ocorreu um erro, o pix não foi criado!' 
          })
        }).finally(() => {
          this.saving = false
          if(req_status)this.goBack()
        })
      }
    },
    customFilter(item, queryText, itemText) {
      let { corporateName, fantasyName } = item.attributes
      const text1 = corporateName ? corporateName.toLowerCase() : ''
      const fname = fantasyName ? fantasyName.toLowerCase() : ''
      const query = queryText.toLowerCase()

      return text1.indexOf(query) > -1 || fname.indexOf(query) > -1
    },
    handleCompanyKeyChange(key) {
      const option = this.sicoob_credential_options.find((item) => item.key === key)
      this.new_pix.company_key = key
      this.new_pix.company = option?.name || null
    },
    getClients(){
      if (this.new_pix.place_id){
        this.loading = true
        let filter = {modality_id: this.new_pix.place_id, short: true}
        Api.Client.index(filter).then(res => res.data).then((clients) => {
          const list = Array.isArray(clients.data) ? clients.data : []
          const ownClientIds = [278, 3648, 3644, 3646, 1675, 3295, 3868, 4285]
          this.all_clients = list.filter(el => {
            const label = `${el.attributes?.name || ''} ${el.attributes?.corporateName || ''}`.toLowerCase()
            return !label.includes('cliente final') && !ownClientIds.includes(el.id) && !el.company_id
          })
        }).finally(()=>{
          this.loading = false
        })
      }
    },
    ...mapActions({
      getStatus: 'Status/index',
    })
  },
  computed: {
    pix_wit_due_date() {
      return this.new_pix.pix_type != 'order'
    },
    today() {
      return new Date().toISOString().substr(0, 10)
    },
    payment_total() {
      return this.parseMoney(this.new_pix.value_aux)
    },
    has_set_payment(){
      return this.payment_total > 0
    },
    bonus_payment_is_valid(){
      // Se for pagamento de bonus não pode sobrar valor
      return (this.is_bonus_payment && this.paying_vrts_ammount == this.payment_total && this.payment_total <= this.client_bonus ) || !this.is_bonus_payment
    },
    vrts_is_selected(){
      return this.selected_vrts_data.paying_vrts.length > 0
    },
    paying_vrts_ammount_is_valid(){
      return Number(this.paying_vrts_ammount) <= this.payment_total
    },
    paying_vrts_ammount(){
      return this.selected_vrts_data.paying_vrts.reduce((sum, vrt) => {
        return sum + (Number(vrt.paying_ammount) || 0) + (Number(vrt.paying_juros) || 0)
      }, 0).toFixed(2)
    },
    bonus_ammount(){
      return Number((this.payment_total - Number(this.paying_vrts_ammount)).toFixed(2))
    },
    sumProgress() {
      if (this.payment_total <= 0) return 0
      return Math.min(100, (Number(this.paying_vrts_ammount) / this.payment_total) * 100)
    },
    pix_ready() {
      if (this.saving || this.loading) return false
      if (!this.new_pix.client_id) return false
      if (!this.new_pix.company_key || !this.hasSicoobCredentialKey(this.new_pix.company_key)) return false
      if (this.payment_total < 20) return false
      if (!this.selected_vrts_data.selected_vrts.length || !this.vrts_is_selected) return false
      return this.paying_vrts_ammount_is_valid && this.bonus_payment_is_valid
    },
    ...mapState({
      modality_name: state => state.App.activeModalityName,
    })
  },
  mounted() {
    this.getSicoobCredentials()
    this.getStatus()
  },
  watch: {
    'new_pix.place_id'(nv, ov){
      if(nv && this.new_pix.place_id > 0 && ov != nv){
        this.getClients();
      }
      this.new_pix.client_id = null
    }
  },
}
</script>

<style scoped>
.charge-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}
.charge-dialog-header {
  padding: 20px 24px;
}
.charge-dialog-header--pix {
  background: linear-gradient(135deg, #00695c 0%, #00897b 55%, #26a69a 100%);
}
.charge-summary-card {
  background: #f7f9fb;
  border: 1px solid #e8eef3;
  border-radius: 12px;
  padding: 12px 14px;
  height: 100%;
}
.charge-sum-box {
  border-radius: 12px;
  padding: 12px 14px;
  border: 1px solid #e8eef3;
  background: #fafbfc;
}
.charge-sum-box--ok {
  border-color: #a5d6a7;
  background: #f1f8f4;
}
.charge-sum-box--err {
  border-color: #ffcc80;
  background: #fff8f0;
}
.charge-empty-box {
  border: 1px dashed #cfd8dc;
  border-radius: 12px;
  background: #fafbfc;
}
</style>
